import { Link, useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";
import { useIsMobile } from "@/hooks/use-mobile";
import { useAuth } from "@/contexts/AuthContext";
import { useUserRoles } from "@/hooks/useUserRoles";
import { useUserProfile } from "@/hooks/useUserProfile";
import { motion } from "framer-motion";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Home, Compass, Calendar, User, LayoutDashboard, Inbox, CalendarCheck, Megaphone, Wallet } from "lucide-react";

type Tab = { to: string; label: string; icon: any; action?: string };

const HIDDEN_ROUTES = ["/auth", "/admin", "/afiliados", "/imovel/", "/reservas/", "/ref/", "/anunciar", "/dashboard", "/configuracoes", "/perfil", "/seguro/imovel/", "/seguradora"];

const triggerHaptic = () => {
  try {
    if (navigator.vibrate) navigator.vibrate(8);
  } catch {}
};

interface MobileBottomTabsProps {
  onMenuOpen?: () => void;
}

export default function MobileBottomTabs({ onMenuOpen }: MobileBottomTabsProps) {
  const { t } = useTranslation();
  const location = useLocation();
  const isMobile = useIsMobile();
  const { user } = useAuth();
  const { isHost, isGuest, isSuperAdmin } = useUserRoles();
  const [isHidden, setIsHidden] = useState(false);

  const { data: unreadCount } = useQuery({
    queryKey: ["unread-notifications-count", user?.id],
    queryFn: async () => {
      if (!user?.id) return 0;
      const { count, error } = await supabase
        .from("push_notifications")
        .select("*", { count: "exact", head: true })
        .eq("user_id", user.id)
        .eq("is_read", false);
      if (error) return 0;
      return count || 0;
    },
    enabled: !!user?.id,
    refetchInterval: 30000,
  });

  const isHostOnly = isHost && !isGuest && !isSuperAdmin;

  const { data: unreadOffersCount } = useQuery({
    queryKey: ["unread-host-offers-count", user?.id],
    queryFn: async () => {
      if (!user?.id) return 0;
      const { count, error } = await supabase
        .from("offer_notifications")
        .select("*", { count: "exact", head: true })
        .eq("host_id", user.id)
        .eq("responded", false);
      if (error) return 0;
      return count || 0;
    },
    enabled: !!user?.id && isHostOnly,
    refetchInterval: 15000,
  });

  const { needsVerification } = useUserProfile();

  useEffect(() => {
    const handleHide = () => setIsHidden(true);
    const handleShow = () => setIsHidden(false);
    const handleUnified = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      setIsHidden(Boolean(detail?.hidden));
    };

    window.addEventListener('hide-bottom-tabs', handleHide);
    window.addEventListener('show-bottom-tabs', handleShow);
    window.addEventListener('berastays:bottom-tabs', handleUnified as EventListener);

    return () => {
      window.removeEventListener('hide-bottom-tabs', handleHide);
      window.removeEventListener('show-bottom-tabs', handleShow);
      window.removeEventListener('berastays:bottom-tabs', handleUnified as EventListener);
    };
  }, []);

  if (!isMobile) return null;
  
  const isAnuncios = location.pathname === "/dashboard/anuncios";
  const isReservas = location.pathname === "/dashboard/reservas" || location.pathname === "/reservas";
  const isFinanceiro = location.pathname === "/dashboard/financeiro";

  if (isAnuncios || isReservas || isFinanceiro) return null;
  
  const isDashboard = location.pathname.startsWith("/dashboard");
  const isExplore = location.pathname === "/explorar";
  
  // Show bottom tabs on both explore and dashboard for host-only users, but only explore for guests
  const shouldShow = isHostOnly ? (isExplore || isDashboard) : isExplore;
  if (!shouldShow) return null;
  if (isHidden) return null;

  const tabs: Tab[] = isHostOnly
    ? [
        { to: "/explorar", label: "Início", icon: Home },
        { to: "/dashboard/reservas", label: "Reservas", icon: CalendarCheck },
        { to: "/dashboard/anuncios", label: "Anúncios", icon: Megaphone },
        { to: "/dashboard/financeiro", label: "Ganhos", icon: Wallet },
      ]
    : [
        { to: "/", label: t('nav.home') || "Início", icon: Home },
        { to: user ? "/explorar" : "/auth", label: t('nav.explore') || "Explorar", icon: Compass },
        { to: user ? "/reservas" : "/auth", label: t('nav.myBookings') || "Reservas", icon: Calendar },
        { to: "#menu", label: "Menu", icon: User, action: "menu" },
      ];

  return (
    <nav
      className="fixed bottom-0 left-0 right-0 z-[100] bg-white dark:bg-[#1C1C1E] border-t border-zinc-100 dark:border-[#2A2A2A] shadow-[0_-4px_20px_rgba(0,0,0,0.03)]"
      id="bera-mobile-bottom-tabs"
      data-route="/explorar"
      style={{
        paddingBottom: "max(env(safe-area-inset-bottom), 6px)",
        paddingTop: "6px",
        transform: "translateZ(0)",
      }}
    >
      <div className="max-w-md mx-auto px-6 flex items-center justify-between">
        {tabs.map((tab) => {
          const isMenuAction = tab.action === "menu";
          const active = (() => {
            if (isMenuAction) return false;
            if (tab.to === "/") return location.pathname === "/";
            
            if (tab.to.includes("?")) {
              const tabParam = tab.to.split("tab=")[1];
              const currentParam = new URLSearchParams(location.search).get("tab") || (location.pathname === "/dashboard" ? "properties" : "");
              return location.pathname === "/dashboard" && currentParam === tabParam;
            }
            
            return location.pathname.startsWith(tab.to);
          })();
          
          const handleClick = (e: React.MouseEvent) => {
            triggerHaptic();
            if (isMenuAction && onMenuOpen) {
              e.preventDefault();
              onMenuOpen();
            }
          };

          const Component = isMenuAction ? "button" : Link;
          const linkProps = isMenuAction ? {} : { to: tab.to };
          const Icon = tab.icon;

          return (
            <Component
              key={`tab-${tab.label}-${tab.to}`}
              {...linkProps as any}
              onClick={handleClick}
              data-tab-id={`tab-${tab.label}`}
              className="relative flex flex-col items-center justify-center shrink-0 flex-1 max-w-[72px] h-[48px] gap-0.5"
            >
              <div className={cn(
                "relative flex items-center justify-center w-8 h-8 rounded-full transition-all duration-300",
                active ? "bg-[#E8513D]/10" : "bg-transparent"
              )}>
                <Icon 
                  className={cn("w-[18px] h-[18px] transition-all duration-300", active ? "text-[#E8513D] scale-105" : "text-gray-500 dark:text-zinc-400")} 
                  strokeWidth={active ? 2.2 : 2}
                />
                
                {needsVerification && tab.action === "menu" && (
                  <span className="absolute -top-0.5 -right-0.5 flex h-2.5 w-2.5">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75" />
                    <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-primary ring-2 ring-white" />
                  </span>
                )}

                {tab.action === "menu" && (unreadCount || 0) > 0 && (
                  <span className="absolute -top-1 -right-1 min-w-[14px] h-[14px] text-[8px] font-bold bg-[#E8513D] text-white rounded-full flex items-center justify-center px-1">
                    {(unreadCount || 0) > 9 ? "9+" : unreadCount}
                  </span>
                )}
                {isHostOnly && tab.to === "/ofertas/recebidas" && (unreadOffersCount || 0) > 0 && (
                  <span className="absolute -top-1 -right-1 min-w-[14px] h-[14px] text-[8px] font-bold bg-[#E8513D] text-white rounded-full flex items-center justify-center px-1">
                    {(unreadOffersCount || 0) > 9 ? "9+" : unreadOffersCount}
                  </span>
                )}
              </div>
              
              <span className={cn(
                "transition-all duration-300 whitespace-nowrap tracking-tight",
                active ? "font-bold text-[9.5px] text-[#E8513D]" : "font-semibold text-[9.5px] text-gray-500 dark:text-zinc-400"
              )}>
                {tab.label}
              </span>
            </Component>
          );
        })}
      </div>
    </nav>
  );
}
